import { onMounted, onUnmounted, type Ref } from 'vue'

const noop = () => undefined

interface InlineAutoplayOptions {
  isEnabled?: () => boolean
  pauseWhenOutOfView?: boolean
}

export function useInlineAutoplayVideo(
  videoRef: Ref<HTMLVideoElement | null>,
  options: InlineAutoplayOptions = {}
) {
  const isEnabled = options.isEnabled ?? (() => true)
  const pauseWhenOutOfView = options.pauseWhenOutOfView ?? false

  let observer: IntersectionObserver | null = null
  let rafId: number | null = null
  let timeoutIds: number[] = []
  let isIntersecting = true

  const configureVideo = () => {
    const video = videoRef.value
    if (!video) return

    video.muted = true
    video.defaultMuted = true
    video.autoplay = true
    video.playsInline = true

    video.setAttribute('muted', '')
    video.setAttribute('autoplay', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    video.setAttribute('x5-video-player-fullscreen', 'false')
  }

  const pauseInlineVideo = () => {
    const video = videoRef.value
    if (!video || video.paused) return
    video.pause()
  }

  const tryPlayInlineVideo = () => {
    const video = videoRef.value
    if (!video || document.hidden || !isEnabled()) return
    if (pauseWhenOutOfView && !isIntersecting) return

    configureVideo()

    const playPromise = video.play()
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch(noop)
    }
  }

  const requestAutoplay = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }

    rafId = requestAnimationFrame(() => {
      tryPlayInlineVideo()
    })
  }

  const scheduleAutoplayBurst = () => {
    requestAutoplay()

    timeoutIds.forEach((id) => window.clearTimeout(id))
    timeoutIds = [
      window.setTimeout(requestAutoplay, 120),
      window.setTimeout(requestAutoplay, 400),
      window.setTimeout(requestAutoplay, 1200)
    ]
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      pauseInlineVideo()
      return
    }

    scheduleAutoplayBurst()
  }

  const handlePageShow = () => {
    scheduleAutoplayBurst()
  }

  const handleUserActivation = () => {
    scheduleAutoplayBurst()
  }

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries
    if (!entry) return

    isIntersecting = entry.isIntersecting

    if (isIntersecting) {
      scheduleAutoplayBurst()
      return
    }

    if (pauseWhenOutOfView) {
      pauseInlineVideo()
    }
  }

  const mediaEvents = ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'] as const

  onMounted(() => {
    const video = videoRef.value
    if (!video) return

    configureVideo()
    mediaEvents.forEach((eventName) => {
      video.addEventListener(eventName, requestAutoplay)
    })

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.25
      })
      observer.observe(video)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('touchstart', handleUserActivation, { passive: true })
    window.addEventListener('click', handleUserActivation, { passive: true })

    scheduleAutoplayBurst()
  })

  onUnmounted(() => {
    const video = videoRef.value

    if (video) {
      mediaEvents.forEach((eventName) => {
        video.removeEventListener(eventName, requestAutoplay)
      })
    }

    observer?.disconnect()

    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handlePageShow)
    window.removeEventListener('touchstart', handleUserActivation)
    window.removeEventListener('click', handleUserActivation)

    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }

    timeoutIds.forEach((id) => window.clearTimeout(id))
    timeoutIds = []
  })

  return {
    configureVideo,
    pauseInlineVideo,
    requestAutoplay
  }
}
